import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
} from "@arcadeai/design-system/rhf";
import { InfoIcon, Key, PlusCircle, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { FieldProps, Server } from "@/app/servers/types";
import { InsertSecretModal } from "./insert-secret-modal";
import {
  buildSecretInsertion,
  collectReferencedSecrets,
  extractSecretNames,
  SECRET_TOKEN_PATTERN,
} from "./secret-utils";
import { HeaderSecretsField } from "./secrets-field";
import { useHeaderDrafts } from "./use-header-drafts";

type HeadersFieldProps = FieldProps & {
  server?: Server;
};

// Constants
const EMPTY_POPOVER_STATE = {
  open: false,
  key: "",
  position: { top: 0, left: 0 },
};

const getPopoverPosition = (element: HTMLInputElement) => ({
  top: element.getBoundingClientRect().bottom + window.scrollY,
  left: element.getBoundingClientRect().left + window.scrollX,
});

export const HeadersField = ({
  form,
  canUpdate,
  server,
}: HeadersFieldProps) => {
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [modalTriggers, setModalTriggers] = useState<Record<string, boolean>>(
    {}
  );
  const [suggestionPopover, setSuggestionPopover] =
    useState(EMPTY_POPOVER_STATE);

  const headers = form.watch("mcp.headers") || {};
  const secrets = form.watch("mcp.secrets") || {};
  const formErrors = form.formState.errors;

  // Initialize drafts hook
  const {
    drafts,
    setDrafts,
    addDraft,
    removeDraft,
    updateDraft,
    commitDraftIfReady,
  } = useHeaderDrafts({ form });

  // Collect all secret references from headers and drafts
  const getReferencedSecrets = useCallback(
    (): Set<string> => collectReferencedSecrets(headers, drafts),
    [headers, drafts]
  );

  // Handle completion of a secret reference (automatically add new secrets)
  const handleSecretCompletion = useCallback(
    (value: string) => {
      const secretNames = extractSecretNames(value);
      const newSecrets = secretNames.filter((name) => !(name in secrets));

      if (newSecrets.length > 0) {
        const updatedSecrets = { ...secrets };
        for (const secretName of newSecrets) {
          updatedSecrets[secretName] = "";
        }
        form.setValue("mcp.secrets", updatedSecrets, { shouldValidate: false });
      }
    },
    [secrets, form]
  );

  // Helper to handle secret suggestion popover logic
  const handleSecretSuggestion = useCallback(
    (
      value: string,
      cursorPosition: number,
      inputElement: HTMLInputElement,
      refKey: string
    ) => {
      const textBeforeCursor = value.slice(0, cursorPosition);
      const justClosedSecret =
        textBeforeCursor.endsWith("}") &&
        textBeforeCursor.includes("${secret:");

      // Handle secret completion
      if (justClosedSecret) {
        handleSecretCompletion(value);
        if (suggestionPopover.open && suggestionPopover.key === refKey) {
          setSuggestionPopover(EMPTY_POPOVER_STATE);
        }
        return;
      }

      // Show/hide suggestion popover
      const matchesSecretToken = SECRET_TOKEN_PATTERN.test(textBeforeCursor);
      const shouldShowPopover = matchesSecretToken;
      const isCurrentlyOpen =
        suggestionPopover.open && suggestionPopover.key === refKey;

      if (shouldShowPopover && !isCurrentlyOpen) {
        setSuggestionPopover({
          open: true,
          key: refKey,
          position: getPopoverPosition(inputElement),
        });
      } else if (!shouldShowPopover && isCurrentlyOpen) {
        setSuggestionPopover(EMPTY_POPOVER_STATE);
      }
    },
    [suggestionPopover, handleSecretCompletion]
  );

  // Sync secrets - add missing ones
  const syncAllSecrets = useCallback(() => {
    const referencedSecrets = getReferencedSecrets();
    const updated = { ...secrets };
    let changed = false;

    // Add missing secrets
    for (const secretName of referencedSecrets) {
      if (!(secretName in updated)) {
        updated[secretName] = "";
        changed = true;
      }
    }

    if (changed) {
      form.setValue("mcp.secrets", updated, { shouldValidate: false });
    }
  }, [secrets, form, getReferencedSecrets]);

  const handleRemoveHeader = useCallback(
    (headerKey: string) => {
      const updatedHeaders = { ...headers };
      delete updatedHeaders[headerKey];
      form.setValue("mcp.headers", updatedHeaders);
      syncAllSecrets();
    },
    [form, headers, syncAllSecrets]
  );

  const handleHeaderKeyChange = useCallback(
    (oldKey: string, newKey: string) => {
      if (oldKey === newKey || !newKey.trim()) {
        return;
      }

      const updatedHeaders = { ...headers };
      const value = updatedHeaders[oldKey];
      delete updatedHeaders[oldKey];
      updatedHeaders[newKey] = value;

      form.setValue("mcp.headers", updatedHeaders);
    },
    [form, headers]
  );

  const handleHeaderValueChange = useCallback(
    (
      headerKey: string,
      value: string,
      cursorPosition?: number,
      inputElement?: HTMLInputElement
    ) => {
      form.setValue(`mcp.headers.${headerKey}`, value, {
        shouldValidate: false,
      });

      if (cursorPosition !== undefined && cursorPosition >= 1 && inputElement) {
        handleSecretSuggestion(
          value,
          cursorPosition,
          inputElement,
          `form:${headerKey}`
        );
      }
    },
    [form, handleSecretSuggestion]
  );

  const insertSecretIntoForm = useCallback(
    (headerKey: string, secretName: string) => {
      const refKey = `form:${headerKey}`;
      const input = inputRefs.current[refKey];
      if (!input) {
        return;
      }
      // Use the current input value, not the form state
      const currentValue = input.value || "";
      const cursorPosition = input.selectionStart || currentValue.length;

      const { newValue, newCursorPosition } = buildSecretInsertion(
        currentValue,
        cursorPosition,
        secretName
      );
      handleHeaderValueChange(headerKey, newValue);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    },
    [handleHeaderValueChange]
  );

  const insertSecretIntoDraft = useCallback(
    (draftId: string, secretName: string) => {
      const refKey = `draft:${draftId}`;
      const input = inputRefs.current[refKey];
      if (!input) {
        return;
      }
      // Use the current input value
      const currentValue = input.value || "";
      const cursorPosition = input.selectionStart || currentValue.length;

      const { newValue, newCursorPosition } = buildSecretInsertion(
        currentValue,
        cursorPosition,
        secretName
      );

      setDrafts((prev) => {
        const next = prev.map((d) => {
          if (d.id !== draftId) {
            return d;
          }
          return { ...d, value: newValue };
        });
        return next;
      });

      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    },
    [setDrafts]
  );

  const handleCreateAndInsertSecret = useCallback(
    (headerKey: string, secretName: string, secretValue: string) => {
      const updatedSecrets = {
        ...secrets,
        [secretName]: secretValue,
      };
      form.setValue("mcp.secrets", updatedSecrets, { shouldValidate: false });

      insertSecretIntoForm(headerKey, secretName);
    },
    [secrets, form, insertSecretIntoForm]
  );

  const updateDraftWithUI = useCallback(
    (options: {
      id: string;
      field: "name" | "value";
      value: string;
      cursorPosition?: number;
      inputElement?: HTMLInputElement;
    }) => {
      const { id, field, value, cursorPosition, inputElement } = options;
      updateDraft({ id, field, value });

      if (
        field === "value" &&
        cursorPosition !== undefined &&
        cursorPosition >= 1 &&
        inputElement
      ) {
        handleSecretSuggestion(
          value,
          cursorPosition,
          inputElement,
          `draft:${id}`
        );
      }
    },
    [updateDraft, handleSecretSuggestion]
  );

  // commitDraftIfReady moved to hook

  const headerEntries = Object.entries(headers);
  const availableSecrets = Object.keys(secrets);

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FormLabel className="font-medium text-base">
              Headers{" "}
              <span className="text-muted-foreground text-xs">(Optional)</span>
            </FormLabel>
            <Tooltip>
              <TooltipTrigger type="button">
                <InfoIcon className="size-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="w-auto max-w-md" side="top">
                <div className="space-y-2">
                  <p className="font-semibold text-sm">
                    HTTP Headers for MCP Servers
                  </p>
                  <p className="text-sm">Common uses:</p>
                  <ul className="list-inside list-disc space-y-1 text-sm">
                    <li>Authentication tokens (API keys, OAuth)</li>
                    <li>Session identifiers</li>
                    <li>Custom metadata</li>
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
          {!!canUpdate && (
            <Button
              className="flex items-center gap-x-1.5"
              onClick={addDraft}
              size="sm"
              type="button"
              variant="outline"
            >
              <PlusCircle className="h-4 w-4 text-green-500" />
              Add Header
            </Button>
          )}
        </div>
        <FormDescription>
          Add custom HTTP headers to include with MCP server requests. Use the
          key icon to insert secrets or type{" "}
          <code className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-xs">
            $&#123;secret:NAME&#125;
          </code>
        </FormDescription>
        <div className="space-y-3">
          {headerEntries.map(([headerKey, headerValue]) => (
            <div className="flex items-center gap-2" key={headerKey}>
              <FormItem className="flex-1">
                <FormControl>
                  <Input
                    defaultValue={headerKey}
                    disabled={!canUpdate}
                    onBlur={(e) => {
                      const newKey = e.target.value.trim();
                      if (newKey && newKey !== headerKey) {
                        handleHeaderKeyChange(headerKey, newKey);
                      }
                    }}
                    placeholder="e.g., Authorization"
                  />
                </FormControl>
              </FormItem>
              <FormItem className="flex-[2]">
                <FormControl>
                  <div className="flex gap-1">
                    <Input
                      className={
                        formErrors.mcp?.headers ? "border-destructive" : ""
                      }
                      disabled={!canUpdate}
                      onBlur={() => {
                        syncAllSecrets();
                      }}
                      onChange={(e) => {
                        const cursorPos = e.target.selectionStart || 0;
                        const newValue = e.target.value;
                        handleHeaderValueChange(
                          headerKey,
                          newValue,
                          cursorPos,
                          e.target
                        );
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          suggestionPopover.open &&
                          suggestionPopover.key === `form:${headerKey}`
                        ) {
                          e.preventDefault();
                          setSuggestionPopover({
                            open: false,
                            key: "",
                            position: { top: 0, left: 0 },
                          });
                          setModalTriggers((prev) => ({
                            ...prev,
                            [`form:${headerKey}`]: true,
                          }));
                        }
                      }}
                      placeholder="e.g., Bearer ${secret:API_TOKEN}"
                      ref={(el) => {
                        inputRefs.current[`form:${headerKey}`] = el;
                      }}
                      value={headerValue}
                    />
                    <InsertSecretModal
                      availableSecrets={availableSecrets}
                      disabled={!canUpdate}
                      onCreateAndInsert={(name, value) =>
                        handleCreateAndInsertSecret(headerKey, name, value)
                      }
                      onInsertSecret={(name) =>
                        insertSecretIntoForm(headerKey, name)
                      }
                      onOpenChange={(isOpen) => {
                        if (!isOpen) {
                          setModalTriggers((prev) => ({
                            ...prev,
                            [`form:${headerKey}`]: false,
                          }));
                        }
                      }}
                      triggerOpen={Boolean(modalTriggers[`form:${headerKey}`])}
                    />
                  </div>
                </FormControl>
              </FormItem>
              {!!canUpdate && (
                <Button
                  className="shrink-0 text-destructive hover:text-destructive/80"
                  onClick={() => handleRemoveHeader(headerKey)}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Remove Header</span>
                </Button>
              )}
            </div>
          ))}
          {drafts.map((d, index) => (
            <div className="flex items-center gap-2" key={d.id}>
              <FormItem className="flex-1">
                <FormControl>
                  <Input
                    autoFocus={index === drafts.length - 1}
                    disabled={!canUpdate}
                    onBlur={() => commitDraftIfReady(d.id)}
                    onChange={(e) =>
                      updateDraft({
                        id: d.id,
                        field: "name",
                        value: e.target.value,
                      })
                    }
                    placeholder="e.g., Authorization"
                    value={d.name}
                  />
                </FormControl>
              </FormItem>
              <FormItem className="flex-[2]">
                <FormControl>
                  <div className="flex gap-1">
                    <Input
                      className={
                        formErrors.mcp?.headers ? "border-destructive" : ""
                      }
                      disabled={!canUpdate}
                      onBlur={() => commitDraftIfReady(d.id)}
                      onChange={(e) => {
                        const cursorPos = e.target.selectionStart || 0;
                        updateDraftWithUI({
                          id: d.id,
                          field: "value",
                          value: e.target.value,
                          cursorPosition: cursorPos,
                          inputElement: e.target,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          suggestionPopover.open &&
                          suggestionPopover.key === `draft:${d.id}`
                        ) {
                          e.preventDefault();
                          setSuggestionPopover({
                            open: false,
                            key: "",
                            position: { top: 0, left: 0 },
                          });
                          setModalTriggers((prev) => ({
                            ...prev,
                            [`draft:${d.id}`]: true,
                          }));
                        }
                      }}
                      placeholder="e.g., Bearer ${secret:API_TOKEN}"
                      ref={(el) => {
                        inputRefs.current[`draft:${d.id}`] = el;
                      }}
                      value={d.value}
                    />
                    <InsertSecretModal
                      availableSecrets={availableSecrets}
                      disabled={!canUpdate}
                      onCreateAndInsert={(name, value) => {
                        const updatedSecrets = {
                          ...secrets,
                          [name]: value,
                        };
                        form.setValue("mcp.secrets", updatedSecrets, {
                          shouldValidate: false,
                        });
                        insertSecretIntoDraft(d.id, name);
                      }}
                      onInsertSecret={(name) =>
                        insertSecretIntoDraft(d.id, name)
                      }
                      onOpenChange={(isOpen) => {
                        if (!isOpen) {
                          setModalTriggers((prev) => ({
                            ...prev,
                            [`draft:${d.id}`]: false,
                          }));
                        }
                      }}
                      triggerOpen={Boolean(modalTriggers[`draft:${d.id}`])}
                    />
                  </div>
                </FormControl>
              </FormItem>
              {!!canUpdate && (
                <Button
                  className="shrink-0 text-destructive hover:text-destructive/80"
                  onClick={() => removeDraft(d.id)}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Remove Header</span>
                </Button>
              )}
            </div>
          ))}
          {!!formErrors.mcp?.headers?.message && (
            <p className="text-destructive text-xs">
              {String(formErrors.mcp.headers.message)}
            </p>
          )}
        </div>
        <div className="mt-6 border-zinc-800 border-t pt-6">
          <HeaderSecretsField
            canUpdate={canUpdate}
            form={form}
            server={server}
            usedSecrets={getReferencedSecrets()}
          />
        </div>
      </div>

      {!!suggestionPopover.open && (
        <Popover open={suggestionPopover.open}>
          <PopoverTrigger
            render={
              <div
                className="pointer-events-none fixed"
                style={{
                  top: suggestionPopover.position.top,
                  left: suggestionPopover.position.left,
                  width: 1,
                  height: 1,
                }}
              />
            }
          />
          <PopoverContent
            align="start"
            className="w-64 p-0"
            initialFocus={false}
            side="bottom"
          >
            <Button
              className="w-full justify-start gap-2"
              onClick={() => {
                setSuggestionPopover({
                  open: false,
                  key: "",
                  position: { top: 0, left: 0 },
                });
                setModalTriggers((prev) => ({
                  ...prev,
                  [suggestionPopover.key]: true,
                }));
              }}
              type="button"
              variant="ghost"
            >
              <Key className="size-4" />
              Create a new header secret
            </Button>
          </PopoverContent>
        </Popover>
      )}
    </TooltipProvider>
  );
};
